import React, { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  CssBaseline,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { useAppDispatch } from '@hooks/redux'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { fetchDevices } from '@store/slices/devicesSlice'
import { fetchAlerts } from '@store/slices/alertsSlice'
import NavDrawer from '@components/navigation/NavDrawer'
import PlusMenu from '@components/navigation/PlusMenu'
import ProfileMenu from '@components/navigation/ProfileMenu'
import NotificationBell from '@components/navigation/NotificationBell'
import BottomNav from '@components/navigation/BottomNav'

const DRAWER_WIDTH = 250

const MainLayout: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const dispatch = useAppDispatch()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [plusMenuAnchor, setPlusMenuAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => {
    dispatch(fetchApiariesAndGroups())
    dispatch(fetchDevices())
    dispatch(fetchAlerts())
  }, [dispatch])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handlePlusMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setPlusMenuAnchor(event.currentTarget)
  }

  const handlePlusMenuClose = () => {
    setPlusMenuAnchor(null)
  }

  // Determine page title based on path
  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/' || path === '/apiaries') return 'Hives'
    if (path === '/hives/create') return 'New Hive'
    if (path.startsWith('/hives/') && path.endsWith('/edit')) return 'Edit Hive'
    if (path === '/apiaries/create') return 'New Apiary'
    if (path.startsWith('/apiaries/') && path.endsWith('/edit')) return 'Edit Apiary'
    if (path === '/inspections/create') return 'New Inspection'
    if (path.includes('/inspections')) return 'Inspections'
    if (path.startsWith('/hives/')) return 'Hive'
    if (path.startsWith('/apiaries/')) return 'Apiary'
    if (path.startsWith('/inspections')) return 'Inspections'
    if (path.startsWith('/measurements')) return 'Trends'
    if (path === '/alertrules/create') return 'New Alert Rule'
    if (path.startsWith('/alertrules/') && path.endsWith('/edit')) return 'Edit Alert Rule'
    if (path.startsWith('/alertrules')) return 'Alert Rules'
    if (path.startsWith('/alerts')) return 'Alerts'
    if (path === '/checklists/create') return 'New Checklist'
    if (path.startsWith('/checklists')) return 'Checklists'
    if (path.startsWith('/devices')) return 'Devices'
    if (path === '/dashboards/create') return 'New Dashboard'
    if (path.startsWith('/dashboards/') && path.endsWith('/edit')) return 'Edit Dashboard'
    if (path.startsWith('/dashboards')) return 'Dashboards'
    if (path.startsWith('/profile')) return 'Profile'
    if (path.startsWith('/research')) return 'Research'
    if (path === '/groups/create') return 'New Group'
    if (path.startsWith('/groups/') && path.endsWith('/edit')) return 'Edit Group'
    if (path.startsWith('/groups')) return 'Groups'
    if (path.startsWith('/export')) return 'Export'
    if (path.startsWith('/import')) return 'Import'
    return 'Smart Nyuki'
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          boxShadow: 'none',
          backgroundColor: '#F8B133', // Primary Yellow
          color: '#242424', // Dark Grey Text
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {getPageTitle()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handlePlusMenuClick}
              sx={{
                color: '#242424',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 2,
                mr: 1,
                width: 36,
                height: 36,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>

            <NotificationBell />

            <ProfileMenu />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <NavDrawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          width={DRAWER_WIDTH}
        />
      </Box>

      {/* Plus Menu */}
      <PlusMenu
        anchorEl={plusMenuAnchor}
        open={Boolean(plusMenuAnchor)}
        onClose={handlePlusMenuClose}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          pb: { xs: 8, md: 3 }, // Add padding bottom for mobile nav
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '56px', // AppBar height
          overflowX: 'hidden',
        }}
      >
        <Outlet />
      </Box>

      <BottomNav />
    </Box>
  )
}

export default MainLayout
